import React, { useState, useContext, useEffect } from 'react';
import DataTypeItem from 'Body/RezRATE/AdminLanding/AdminCandidates/DataTypeItem';
import Loading from 'Body/Statuses/Loading';
import { DepartmentContext } from 'DepartmentWrapper';
import { fetchDataAgnostic, putData } from 'Services/dataApi';
import { checkLoginForError, formatBearerToken } from 'Common.functions';
import { useAuth0 } from 'auth0/reactAuth0Spa';
import { prepareDataTypesForSortUpdate, separateDataTypesByAvailability } from 'Services/candidates';

import style from './ManageFields.style';
import { isSettingsChangeBlocked, getConflictedFieldForChanges, fieldSettingsErrorMessage } from 'Services/fieldConfiguration';
import { Button, Input, InputGroup, InputGroupText } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';

const ManageFields = ({ onSaved, windowWidth }) => {
  const [dataTypes, setDataTypes] = useState({ enabled: [], disabled: [] });
  const [dataTypesRaw, setDataTypesRaw] = useState([]);
  const [settingsError, setSettingsError] = useState('');
  const [isUpdatingDataType, setIsUpdatingDataType] = useState(false);
  const [isInitializedDataTypes, setIsInitializedDataTypes] = useState(false);
  const [availableFieldsSearchString, setAvailableFieldsSearchString] = useState('');
  const widthToSwitchToMobile = 980;

  const dContext = useContext(DepartmentContext);
  const { getTokenSilently, loginWithRedirect } = useAuth0();

  useEffect(() => {
    getDepartmentDataTypes();
  }, []);

  const getDepartmentDataTypes = () => {
    setIsUpdatingDataType(true);
    getTokenSilently()
      .then((token) => {
        fetchDataAgnostic(
          'department/dataTypes',
          { pk_Department: dContext.department.pk_Department },
          formatBearerToken(token),
        )
          .then((res) => {
            setIsInitializedDataTypes(true);
            setIsUpdatingDataType(false);
            setDataTypes(separateDataTypesByAvailability(res.data));
            setDataTypesRaw(res.data);
          })
          .catch((err) => {
            setIsUpdatingDataType(false);
          });
      })
      .catch((err) => {
        setIsUpdatingDataType(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const updateDataType = (dataTypeToUpdate, updateSortOrders) => {
    setSettingsError('');
    const conflictField = getConflictedFieldForChanges(dataTypesRaw, dataTypeToUpdate);
    const fieldSettingsContext = {
      field: conflictField,
      departmentId: dContext.department.pk_Department,
    };
    if (isSettingsChangeBlocked(dataTypesRaw, dataTypeToUpdate)) {
      setSettingsError(fieldSettingsErrorMessage({ code: 'FIELD_CONFIGURATION_CONFLICT' }, fieldSettingsContext));
      return;
    }
    if (updateSortOrders) {
      dataTypeToUpdate = prepareDataTypesForSortUpdate(dataTypes, dataTypeToUpdate);
    }

    setIsUpdatingDataType(true);
    getTokenSilently()
      .then((token) => {
        putData(
          'department/dataTypes',
          { pk_Department: dContext.department.pk_Department },
          { types: dataTypeToUpdate },
          formatBearerToken(token),
          { suppressErrorLog: true },
        )
          .then((res) => {
            setIsUpdatingDataType(false);
            getDepartmentDataTypes();
            if (!res || !res.data || res.data.error !== false) {
              setSettingsError(fieldSettingsErrorMessage(res && res.data, fieldSettingsContext));
              if (onSaved) onSaved(true);
              return;
            }
            if (onSaved) {
              onSaved();
            }
          })
          .catch((err) => {
            setSettingsError(fieldSettingsErrorMessage(err && err.response && err.response.data, fieldSettingsContext));
            setIsUpdatingDataType(false);
            getDepartmentDataTypes();
            if (onSaved) {
              onSaved(true);
            }
          });
      })
      .catch((err) => {
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const renderManageFields = () => {
    const { enabled = [], disabled = [] } = dataTypes;
    const count = enabled ? enabled.length : 0;
    let index = 1;

    const selectOptions = [];

    while (index <= count) {
      selectOptions.push(
        <option key={index} value={index}>
          {index}
        </option>,
      );
      index++;
    }

    const enabledList = enabled.map((dataType) => {
      return (
        <DataTypeItem
          dataType={dataType}
          isUpdatingDataType={isUpdatingDataType}
          key={dataType.Name}
          departmentId={dContext.department.pk_Department}
          numberOfEnabled={count}
          selectOptions={selectOptions}
          updateDataType={updateDataType}
          windowWidth={windowWidth}
        />
      );
    });

    const disabledList = disabled
      .filter((d) => {
        return (
          !availableFieldsSearchString ||
          d.Name.toLowerCase().indexOf(availableFieldsSearchString.toLowerCase()) >= 0 ||
          d.Alias.toLowerCase().indexOf(availableFieldsSearchString.toLowerCase()) >= 0
        );
      })
      .map((dataType, i) => {
        return (
          <DataTypeItem
            key={dataType.Name}
            dataType={dataType}
            departmentId={dContext.department.pk_Department}
            numberOfEnabled={count}
            enabled={enabled}
            updateDataType={updateDataType}
            isUpdatingDataType={isUpdatingDataType}
            selectOptions={selectOptions}
          />
        );
      });

    if (!isInitializedDataTypes && isUpdatingDataType) {
      return (
        <div
          style={{
            width: '100%',
            height: '40vh',
            fontWeight: 'bold',
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
          }}
        >
          <div style={{ height: 100, width: 100 }}>
            <Loading
              options={{
                labelText: 'Loading. . .',
              }}
            />
          </div>
        </div>
      );
    }

    return (
      <>
        {settingsError ? <div role="alert">{settingsError}</div> : null}
        <div style={{ ...style.simpleColumn, width: '100%', height: '100%' }}>
          <div style={style.simpleRow}>
            <div
              style={{
                ...style.simpleColumn,
                width: '40%',
                margin: windowWidth < widthToSwitchToMobile ? 0 : 5,
                textAlign: 'center',
                fontWeight: 'bold',
              }}
            >
              Inactive Fields
            </div>
            <div
              style={{
                ...style.simpleColumn,
                width: '60%',
                margin: windowWidth < widthToSwitchToMobile ? 0 : 5,
                textAlign: 'center',
                fontWeight: 'bold',
              }}
            >
              Active Fields
            </div>
          </div>
          <div style={style.spacedBetweenRow}>
            <div
              style={{
                ...style.manageFieldsContainer,
                width: '40%',
                overflowY: 'hidden',
                margin: windowWidth < widthToSwitchToMobile ? 0 : 5,
              }}
            >
              <div
                style={{
                  ...style.manageFieldsEnabledItemsVisibilityLabels,
                  justifyContent: 'center',
                  width: '100%',
                  marginBottom: 10,
                }}
              >
                <InputGroup>
                  
                    <Button
                      onClick={() => {
                        setAvailableFieldsSearchString('');
                      }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  
                  <Input
                    type={'text'}
                    value={availableFieldsSearchString}
                    onChange={(e) => {
                      setAvailableFieldsSearchString(e.target.value);
                    }}
                  />
                  
                    <Button color="success" style={{ cursor: 'default' }}>
                      <FontAwesomeIcon icon={faSearch} />
                    </Button>
                  
                </InputGroup>
              </div>
              <div style={{ ...style.simpleRow, overflowY: 'scroll', overflowX: 'clip', height: '100%' }}>
                <div
                  style={{
                    ...style.manageFieldsContainerSubContainer,
                    margin: windowWidth < widthToSwitchToMobile ? 0 : null,
                  }}
                >
                  {disabledList && disabledList.length > 0 ? (
                    disabledList
                  ) : (
                    <div style={{ display: 'flex', width: '100%', textAlign: 'center', justifyContent: 'center' }}>
                      No{availableFieldsSearchString ? ' Matching ' : ' '}Fields
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div
              style={{
                ...style.manageFieldsContainer,
                width: '60%',
                overflowY: 'hidden',
                margin: windowWidth < widthToSwitchToMobile ? 0 : 5,
              }}
            >
              {windowWidth < widthToSwitchToMobile ? null : (
                <div style={style.manageFieldsEnabledItemsVisibilityLabels}>
                  <div style={{ ...style.enabledHeaderLabel, ...style.enabledHeaderRemove }}>Remove</div>
                  <div style={{ ...style.enabledHeaderLabel, ...style.enabledHeaderSort }}>Sort</div>
                  <div style={{ ...style.enabledHeaderLabel, ...style.enabledHeaderType }}>Type</div>
                  <div style={{ ...style.enabledHeaderLabel, ...style.enabledHeaderField }}>Field</div>
                  <div style={{ ...style.enabledHeaderLabel, ...style.enabledHeaderVisibility }}>Rank</div>
                  <div style={{ ...style.enabledHeaderLabel, ...style.enabledHeaderVisibility }}>Eval</div>
                </div>
              )}
              <div style={{ ...style.simpleRow, overflowY: 'scroll', overflowX: 'clip' }}>
                <div style={style.manageFieldsContainerSubContainer}>
                  {enabledList && enabledList.length > 0 ? (
                    enabledList
                  ) : (
                    <div style={{ display: 'flex', width: '100%', textAlign: 'center', justifyContent: 'center' }}>
                      No Fields
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return renderManageFields();
};

export default ManageFields;
